/**
 * Policy Edit Page
 * Page for editing an existing policy draft
 */

import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PolicyEditForm } from '@/components/policies/policy-edit-form'
import { usePolicy, useUpdatePolicy } from '@/hooks/use-policies'
import type { UpdatePolicyRequest } from '@/types/policy'
import { cn } from '@/lib/utils'

/**
 * Loading skeleton for policy edit page
 */
function PolicyEditSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Name field skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
        {/* Description field skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-24 w-full" />
        </div>
        {/* XACML field skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-64 w-full" />
        </div>
        {/* Buttons skeleton */}
        <div className="flex justify-end gap-3 pt-4">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-32" />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Policy Edit Page Component
 */
export function PolicyEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Fetch policy data
  const { data: policy, isLoading, isError, error, refetch } = usePolicy(id)

  // Update mutation
  const updateMutation = useUpdatePolicy(id ?? '')

  // Handle form submission
  const handleSubmit = (data: UpdatePolicyRequest) => {
    if (!id) return

    updateMutation.mutate(data, {
      onSuccess: () => {
        navigate(`/pac/policies/${id}`)
      },
    })
  }

  // Handle cancel
  const handleCancel = () => {
    navigate(`/pac/policies/${id}`)
  }

  // Check if policy is editable (DRAFT or INACTIVE status)
  const isEditable =
    policy?.status === 'DRAFT' || policy?.status === 'INACTIVE'

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/pac/policies/${id}`)}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Separator orientation="vertical" className="mx-2 h-4" />
          <h1 className="text-base font-medium">Edit Policy</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
          className="h-8"
        >
          <RefreshCw
            className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
          />
          Refresh
        </Button>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load policy:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && <PolicyEditSkeleton />}

        {/* Not Editable Warning */}
        {policy && !isLoading && !isEditable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This policy cannot be edited because it has status &quot;
              {policy.status}&quot;. Only policies with status DRAFT or
              INACTIVE can be edited.
            </AlertDescription>
          </Alert>
        )}

        {/* Edit Form */}
        {policy && !isLoading && isEditable && (
          <Card>
            <CardHeader>
              <CardTitle>Edit Policy: {policy.name}</CardTitle>
              <CardDescription>
                Update the policy name, description, or XACML content
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PolicyEditForm
                policy={policy}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isSubmitting={updateMutation.isPending}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

export default PolicyEditPage
